<!-- WebLogSearch.vue -->
<template>
  <div class="py-4 container-fluid">
    <div class="row g-4 align-items-start">
      <!-- 좌측: 필터 -->
      <div class="col-12 col-xl-3">
        <aside class="card shadow-sm filters-sticky">
          <div class="card-body pb-2">
            <h6 class="mb-1" style="font-weight: 800;">검색조건</h6>

            <div class="text-secondary text-xs mb-4" v-if="showStats">
              총 {{ prettyNumber(totalCnt) }} 건이 검색되었습니다. ({{ elapsed }} seconds)
            </div>
            <div class="text-secondary text-xs mb-4" v-else>
              접속로그를 필터링하여 조회할 수 있습니다.
            </div>

            <!-- 기본 조건 -->
            <div class="mb-3 filter-box">
              <div class="filter-box-label">검색조건</div>
              <div class="my-1 d-grid gap-2">
                <input
                  class="form-control form-control-sm"
                  placeholder="아이디"
                  v-model.trim="form.query"
                />

                <select
                  class="form-select form-select-sm"
                  v-model="form.method"
                >
                  <option :value="''">메서드 선택</option>
                  <option disabled value="-">------------------------------------</option>
                  <option v-for="m in methods" :key="m" :value="m">{{ m }}</option>
                </select>
              </div>
            </div>

            <!-- 기간 -->
            <div class="mb-3 filter-box">
              <div class="filter-box-label">시간설정</div>

              <div class="mt-1 d-flex flex-wrap gap-2">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm px-3 mb-2"
                  :class="{ active: activePreset === 'today' }"
                  @click="quickRange('today')"
                >오늘</button>

                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm px-3 mb-2"
                  :class="{ active: activePreset === '7d' }"
                  @click="quickRange('7d')"
                >최근 7일</button>

                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm px-3 mb-2"
                  :class="{ active: activePreset === '30d' }"
                  @click="quickRange('30d')"
                >최근 30일</button>
              </div>

              <div class="rowg-2">
                <div class="col-12 mb-1">
                  <label for="timeFrom" class="section-label text-xs mb-1 ms-0">검색시작</label>
                  <input
                    id="timeFrom"
                    type="datetime-local"
                    class="form-control form-control-sm"
                    v-model="form.timeFrom"
                  />
                </div>
                <div class="col-12">
                  <label for="timeTo" class="section-label text-xs mb-1 ms-0">검색종료</label>
                  <input
                    id="timeTo"
                    type="datetime-local"
                    class="form-control form-control-sm"
                    v-model="form.timeTo"
                  />
                </div>
              </div>
            </div>

            <!-- ACTIONS -->
            <div class="row pt-1 g-2">
              <div class="col-4">
                <button class="btn btn-outline-secondary w-100" type="button" @click="resetForm">
                  초기화
                </button>
              </div>
              <div class="col-8">
                <button class="btn btn-success w-100" type="button" @click="apply">
                  검색
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <!-- 우측: 결과 -->
      <div class="col-12 col-xl-9">
        <RouterView v-slot="{ Component, route }">
          <Transition name="slide-fade" mode="out-in" appear>
            <KeepAlive include="WebLogTable">
              <component
                :is="Component"
                :key="route.meta.keepAlive ? route.name : route.fullPath"
                v-bind="route.name === 'WebLogList' ? { filters: form, applyTick } : {}"
                @update-stats="onUpdateStats"
                @row-click="onRowClick"
              />
            </KeepAlive>
          </Transition>
        </RouterView>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WebLogSearch',
  data() {
    return {
      totalCnt: 0,
      elapsed: 0,
      activePreset: '',
      applyTick: 0,
      showStats: false,
      methods: ['GET', 'POST', 'PUT', 'DELETE'],
      form: {
        query: '',
        method: '',
        timeFrom: '',
        timeTo: '',
      },
    };
  },
  mounted() {
    this.form = { ...this.form };
    if (!this.form.timeFrom && !this.form.timeTo) this.quickRange('today');
    if (this.$route.name === 'WebLogList') this.autoApply();
  },
  methods: {
    prettyNumber(n = 0) {
      try { return Number(n).toLocaleString(); } catch { return n; }
    },
    async ensureListRoute() {
      if (this.$route.name !== 'WebLogList') {
        await this.$router.push({ name: 'WebLogList' }).catch(() => {});
        await new Promise(r => this.$nextTick(r));
      }
    },
    async autoApply() {
      if (this.$route.name === 'WebLogList') {
        this.applyTick++;
      }
    },
    async apply() {
      await this.ensureListRoute();
      this.applyTick++;
    },
    resetForm() {
      this.form = { query: '', method: '', timeFrom: '', timeTo: '' };
      this.quickRange('today');
    },
    quickRange(preset) {
      const now = new Date();
      const toISO = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      let from = new Date();
      if (preset === 'today')      from.setHours(0, 0, 0, 0);
      else if (preset === '7d')    from = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
      else if (preset === '30d')   from = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
      this.form.timeFrom = toISO(from);
      this.form.timeTo   = toISO(now);
      this.activePreset  = preset;
    },
    onUpdateStats({ total, elapsed }) {
      this.totalCnt = total;
      this.elapsed  = elapsed;
      this.showStats = true;
    },
    onRowClick(item) {
      const id = item?.webLogId;
      if (!id) return;
      this.$router.push({ name: 'WebLogDetail', params: { id } });
    },
  },
};
</script>

<style scoped>
.filters-sticky { position: sticky; top: var(--sticky-top, 88px); border-radius: 1rem; }
.section-label { font-size: .72rem; font-weight: 700; letter-spacing: .06em; color: var(--bs-secondary); }
.filter-box { position: relative; border: 1px solid rgba(0,0,0,.125); border-radius: .5rem; padding: 1rem .75rem .75rem; }
.filter-box-label { position: absolute; top: -0.6rem; left: 0.75rem; background: #fff; padding: 0 .5rem; font-size: .8rem; font-weight: 700; color: var(--bs-secondary); }
.btn-outline-secondary.active { background-color: #2563eb !important; border-color: #4c98ca !important; color: #fff !important; }
.slide-fade-enter-active, .slide-fade-leave-active { transition: opacity .22s ease, transform .22s ease; }
.slide-fade-enter-from { opacity: 0; transform: translateX(16px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-16px); }
@media (prefers-reduced-motion: reduce) {
  .slide-fade-enter-active, .slide-fade-leave-active { transition: none; }
}
</style>
